import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { AppShell } from "./AppShell";
import { CallModelStack } from "./CallModelStack";
import { CallSummary } from "./CallSummary";
import { EmptyState, LoadingState } from "./components/states";
import { API_URL, fmtDuration, fmtPhone, fmtTime, prettyJson, relTime, suppressNavOnSelection } from "./lib/api";

type CallSummary = {
  id: number;
  direction: "inbound" | "outbound" | "copilot";
  from_number: string;
  to_number: string;
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  is_test: number;
  recording_url?: string | null;
  customer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  customer_phone: string | null;
  /** Derived server-side. 'live' when the call has activity in last 60s,
   *  'orphaned' when ended_at IS NULL but no activity (bot crashed
   *  mid-call), 'ended' for normally-finalised rows. */
  activity_status: "live" | "orphaned" | "ended";
  /** Derived server-side. 'agent' = listen-only fork (human picked up),
   *  'ai_fallback' = AI ran on the agent-desk number (Dial bridge failed),
   *  'ai' = AI on any other number. */
  handled_by: "agent" | "ai_fallback" | "ai";
};

type CallDetail = CallSummary & {
  recording_url: string | null;
  transcript: string | null;
  notes: string | null;
  telnyx_call_control_id: string | null;
  /** JSON-serialised provider/model snapshot the bot wrote at call start. */
  model_snapshot: string | null;
};

type Transcript = {
  id: number;
  ts: string;
  role: "customer" | "agent" | "assistant" | "system";
  text: string;
  source: string;
};

type ToolCall = {
  id: number;
  ts: string;
  name: string;
  arguments: string | null;
  result: string | null;
  status: "pending" | "completed" | "errored";
  duration_ms: number | null;
  source: string;
};

type LogEntry = {
  id: number;
  ts: string;
  level: "debug" | "info" | "warn" | "error";
  source: "pipecat" | "telnyx" | "api";
  message: string;
  data: string | null;
};

type OutcomeFilter = "all" | "saved" | "churned" | "escalated" | "unresolved";

const OUTCOME_FILTERS: { value: OutcomeFilter; label: string }[] = [
  { value: "all",         label: "All" },
  { value: "saved",       label: "Saved" },
  { value: "churned",     label: "Churned" },
  { value: "escalated",   label: "Escalated" },
  { value: "unresolved",  label: "Open" },
];

// Independent filter axis: who handled the call (Agent / AI / Fallback).
// Lets an operator answer "show me only the calls where AI fell back
// because nobody picked up" with one click.
type HandledByFilter = "all" | "agent" | "ai" | "ai_fallback";

const HANDLED_BY_FILTERS: { value: HandledByFilter; label: string }[] = [
  { value: "all",         label: "All" },
  { value: "agent",       label: "Agent" },
  { value: "ai",          label: "AI" },
  { value: "ai_fallback", label: "Fallback" },
];

const SHOW_TESTS_KEY = "inbound:showTests";

export function Inbound({ callId }: { callId?: number }) {
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [filter, setFilter] = useState<OutcomeFilter>("all");
  const [handledByFilter, setHandledByFilter] = useState<HandledByFilter>("all");
  const [showTests, setShowTests] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_TESTS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [, setLocation] = useLocation();

  // Initial load + SSE for the list. By default is_test=0 so /scenarios
  // synthetic runs don't pollute the production-feeling view; the
  // "Show test runs" toggle drops the filter and badges those rows TEST.
  useEffect(() => {
    let cancelled = false;
    const filterParam = showTests ? "" : "&is_test=0";
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls?limit=100${filterParam}`);
        const d = (await r.json()) as { calls: CallSummary[] };
        if (!cancelled) setCalls(d.calls);
      } catch {
        /* swallow — SSE will catch up */
      }
    })();

    const streamUrl = showTests
      ? `${API_URL}/api/calls/stream`
      : `${API_URL}/api/calls/stream?is_test=0`;
    const es = new EventSource(streamUrl);
    es.addEventListener("calls", (ev) => {
      const d = JSON.parse((ev as MessageEvent).data) as { calls: CallSummary[] };
      setCalls(d.calls);
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [showTests]);

  function toggleShowTests(next: boolean) {
    setShowTests(next);
    try {
      localStorage.setItem(SHOW_TESTS_KEY, next ? "1" : "0");
    } catch {
      /* private mode — toggle still works for the session */
    }
  }

  // Group: live (activity in last 60s) at top, then everything else by
  // recency. Orphaned calls (ended_at IS NULL but bot died → no activity
  // in 60s) drop into "recent" with a different badge so they don't
  // pulse as live and don't take up the operator's attention. Apply
  // outcome filter only to non-live calls; live calls always show
  // because the operator wants to see live activity regardless.
  const { live, recent } = useMemo(() => {
    const live: CallSummary[] = [];
    const recent: CallSummary[] = [];
    for (const c of calls) {
      // Old API responses may not include activity_status (e.g. the
      // page was loaded before the API redeployed). Fall back to the
      // legacy "ended_at IS NULL" so we don't suddenly stop showing
      // live calls when the field is missing.
      const isLive =
        c.activity_status === "live" ||
        (c.activity_status === undefined && c.ended_at === null);
      // Apply the handled_by filter to BOTH live and recent — an
      // operator who's filtered to "Fallback" doesn't want the
      // currently-live agent call to leak through. Fall through (true)
      // when the field isn't present (older API) so the filter doesn't
      // hide everything during a stale page load.
      const handledByMatches =
        handledByFilter === "all" ||
        c.handled_by === undefined ||
        c.handled_by === handledByFilter;
      if (!handledByMatches) continue;
      if (isLive) live.push(c);
      else if (filter === "all" || c.outcome === filter) recent.push(c);
    }
    return { live, recent };
  }, [calls, filter, handledByFilter]);

  // Auto-select first active call (or most recent visible) if none selected.
  useEffect(() => {
    if (callId !== undefined) return;
    const target = live[0] ?? recent[0];
    if (target) setLocation(`/inbound/${target.id}`, { replace: true });
  }, [callId, live, recent, setLocation]);

  return (
    <AppShell>
      <div id="inbound-root" className="inbound">
        <CallList
          live={live}
          handledByFilter={handledByFilter}
          onHandledByFilterChange={setHandledByFilter}
          recent={recent}
          filter={filter}
          onFilterChange={setFilter}
          selectedId={callId}
          showTests={showTests}
          onToggleShowTests={toggleShowTests}
        />
        {callId !== undefined ? (
          <CallDetailPane callId={callId} />
        ) : (
          <div id="inbound-empty" className="inbound-empty">
            <EmptyState
              id="inbound-empty-state"
              title="No calls selected"
              hint="Pick a call from the list to see its receipt, transcript, tools, and logs."
            />
          </div>
        )}
      </div>
    </AppShell>
  );
}

function CallList({
  live,
  recent,
  filter,
  onFilterChange,
  handledByFilter,
  onHandledByFilterChange,
  selectedId,
  showTests,
  onToggleShowTests,
}: {
  live: CallSummary[];
  recent: CallSummary[];
  filter: OutcomeFilter;
  onFilterChange: (f: OutcomeFilter) => void;
  handledByFilter: HandledByFilter;
  onHandledByFilterChange: (f: HandledByFilter) => void;
  selectedId: number | undefined;
  showTests: boolean;
  onToggleShowTests: (next: boolean) => void;
}) {
  const total = live.length + recent.length;
  return (
    <aside id="inbound-call-list" className="call-list">
      <header className="pane-header pane-header-with-toggle">
        <span>INBOUND CALLS</span>
        <label className="show-tests-toggle" title="Include /scenarios test runs">
          <input
            id="inbound-show-tests-toggle"
            type="checkbox"
            checked={showTests}
            onChange={(e) => onToggleShowTests(e.target.checked)}
          />
          <span>Show test runs</span>
        </label>
      </header>

      <div className="call-filters">
        {OUTCOME_FILTERS.map((f) => (
          <button
            key={f.value}
            id={`inbound-filter-${f.value}`}
            type="button"
            className={`call-filter ${filter === f.value ? "is-active" : ""}`}
            onClick={() => onFilterChange(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Independent filter axis for who handled the call. Sits below
       *  the outcome filters so the two read as separate dimensions
       *  (operators want to combine them, e.g. "Saved + Agent"). */}
      <div className="call-filters call-filters-handled-by">
        {HANDLED_BY_FILTERS.map((f) => (
          <button
            key={f.value}
            id={`inbound-filter-handled-${f.value}`}
            type="button"
            className={`call-filter ${handledByFilter === f.value ? "is-active" : ""}`}
            onClick={() => onHandledByFilterChange(f.value)}
            title={
              f.value === "agent"
                ? "Calls a human agent picked up via the SIP bridge."
                : f.value === "ai_fallback"
                  ? "Agent didn't pick up — call fell through to AI overflow."
                  : f.value === "ai"
                    ? "Calls AI handled end-to-end (no human in loop)."
                    : "All calls regardless of who handled them."
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {total === 0 && <div id="inbound-call-list-empty" className="call-row-empty">No calls.</div>}

      {live.length > 0 && (
        <>
          <div className="call-section-label">
            <span className="pulse" /> Live ({live.length})
          </div>
          {live.map((c) => (
            <CallRow key={c.id} call={c} selectedId={selectedId} />
          ))}
        </>
      )}

      {recent.length > 0 && (
        <>
          {live.length > 0 && (
            <div className="call-section-label">Recent ({recent.length})</div>
          )}
          {recent.map((c) => (
            <CallRow key={c.id} call={c} selectedId={selectedId} />
          ))}
        </>
      )}
    </aside>
  );
}

// Visual marker for who handled (or is handling) a call. Three states:
//   agent          listen-only fork was the only pipeline that ran.
//                  Human picked up via the SIP bridge.
//   ai_fallback    AI ran on the agent-desk number — the bridge failed
//                  (no-answer / busy / failed) and the action callback
//                  routed the customer to AI overflow. Operator should
//                  notice these so coverage gaps are visible.
//   ai             AI on any other number (805 inbound, 213 outbound).
function HandledByBadge({
  handledBy,
}: {
  handledBy: CallSummary["handled_by"] | undefined;
}) {
  // Older API responses without the field — render nothing rather than
  // a misleading "AI" default.
  if (!handledBy) return null;
  const label =
    handledBy === "agent"
      ? "Agent"
      : handledBy === "ai_fallback"
        ? "AI · Fallback"
        : "AI";
  const title =
    handledBy === "agent"
      ? "Human agent picked up — the listen-only fork captured this call's audio."
      : handledBy === "ai_fallback"
        ? "Agent didn't pick up; the call fell through to AI overflow."
        : "AI handled this call end-to-end.";
  return (
    <span
      className={`tag-handled-by tag-handled-by-${handledBy}`}
      title={title}
    >
      {label}
    </span>
  );
}

function CallRow({
  call,
  selectedId,
}: {
  call: CallSummary;
  selectedId: number | undefined;
}) {
  const live = call.activity_status === "live";
  const orphaned = call.activity_status === "orphaned";
  const name =
    [call.first_name, call.last_name].filter(Boolean).join(" ") ||
    call.customer_phone ||
    call.from_number;
  // Inbound: caller's number matters — "to" is always our number. Outbound
  // would flip; this component lives in Inbound only.
  return (
    <Link
      id={`inbound-call-row-${call.id}`}
      href={`/inbound/${call.id}`}
      className={`call-row ${selectedId === call.id ? "is-selected" : ""}`}
      onClick={suppressNavOnSelection}
    >
      <div className="call-row-top">
        <span
          className={`call-status ${live ? "is-active" : orphaned ? "is-orphaned" : "is-done"}`}
          title={
            orphaned
              ? "Bot stopped writing logs >60s ago without finalising the row — likely a reload mid-call. Treat as ended."
              : undefined
          }
        >
          {live ? <span className="pulse" /> : null}
          {live ? "Live" : orphaned ? "orphaned" : call.outcome ?? "ended"}
        </span>
        {call.is_test === 1 && (
          <span className="call-row-test-badge" title="from /scenarios test run">
            TEST
          </span>
        )}
        <HandledByBadge handledBy={call.handled_by} />
        <span className="call-row-time">{relTime(call.started_at)}</span>
      </div>
      <div className="call-row-name">{name}</div>
      <div className="call-row-num">{fmtPhone(call.from_number)}</div>
    </Link>
  );
}

export function CallDetailPane({ callId }: { callId: number }) {
  const [, setLocation] = useLocation();
  const [call, setCall] = useState<CallDetail | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [tab, setTab] = useState<"summary" | "transcript" | "tools" | "logs">("summary");
  const [deleting, setDeleting] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);

  async function deleteCall() {
    if (!call) return;
    if (!confirm(`Delete call from ${call.from_number}? This removes its transcripts, tool calls, and logs.`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/calls/${callId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // SSE on the list will refresh /inbound naturally; navigate away from
      // this now-deleted detail page.
      setLocation("/inbound");
    } catch (e) {
      alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
      setDeleting(false);
    }
  }

  // Reset state on call change, then prime via REST and subscribe via SSE.
  useEffect(() => {
    setCall(null);
    setTranscripts([]);
    setToolCalls([]);
    setLogs([]);

    let cancelled = false;
    (async () => {
      const [callRes, tRes, tcRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/calls/${callId}`),
        fetch(`${API_URL}/api/calls/${callId}/transcripts`),
        fetch(`${API_URL}/api/calls/${callId}/tool-calls`),
        fetch(`${API_URL}/api/calls/${callId}/logs`),
      ]);
      if (cancelled) return;
      if (callRes.ok) setCall((await callRes.json()) as CallDetail);
      if (tRes.ok) setTranscripts((await tRes.json()).transcripts);
      if (tcRes.ok) setToolCalls((await tcRes.json()).tool_calls);
      if (lRes.ok) setLogs((await lRes.json()).logs);
    })();

    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("transcript", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as Transcript;
      setTranscripts((cur) => (cur.some((x) => x.id === row.id) ? cur : [...cur, row]));
    });
    es.addEventListener("tool_call", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as ToolCall;
      setToolCalls((cur) => {
        const idx = cur.findIndex((x) => x.id === row.id);
        if (idx === -1) return [...cur, row];
        const copy = cur.slice();
        copy[idx] = row;
        return copy;
      });
    });
    es.addEventListener("log", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as LogEntry;
      setLogs((cur) => (cur.some((x) => x.id === row.id) ? cur : [...cur, row]));
    });
    es.addEventListener("call", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as Partial<CallDetail>;
      setCall((cur) => (cur ? { ...cur, ...row } : cur));
    });

    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  // Auto-scroll transcript to bottom on new turn.
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [transcripts.length, tab]);

  if (!call) {
    return (
      <section id="inbound-call-detail" className="call-detail">
        <LoadingState id="inbound-call-detail-loading" label="Loading call…" />
      </section>
    );
  }

  const live = call.activity_status === "live";
  const orphaned = call.activity_status === "orphaned";
  const name =
    [call.first_name, call.last_name].filter(Boolean).join(" ") ||
    call.customer_phone ||
    call.from_number;

  return (
    <section id="inbound-call-detail" className="call-detail">
      <header id="inbound-detail-header" className="detail-header">
        <div>
          <div className="detail-name">{name}</div>
          <div className="detail-sub">
            {fmtPhone(call.from_number)} → {fmtPhone(call.to_number)} · {call.direction} ·{" "}
            <span className={live ? "tag-live" : orphaned ? "tag-orphaned" : "tag-done"}>
              {live ? "Live" : orphaned ? "orphaned" : call.outcome ?? "ended"}
            </span>{" "}
            · <HandledByBadge handledBy={call.handled_by} />
          </div>
        </div>
        <div className="detail-actions">
          <div className="detail-times">
            <div>started {fmtTime(call.started_at)}</div>
            <div>
              duration{" "}
              {fmtDuration(call.started_at, call.ended_at)}
            </div>
          </div>
          <button
            id="inbound-delete-call-btn"
            className="btn-danger"
            onClick={deleteCall}
            // Block delete only on genuinely live calls — orphans are
            // safe to delete (the bot's gone; nothing's writing to
            // them anymore). The user explicitly wants orphan cleanup
            // to be a one-click op from this row.
            disabled={deleting || live}
            title={live ? "Cannot delete a live call" : "Delete this call and its data"}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </header>

      <CallModelStack snapshot={call.model_snapshot} />

      {!live && call.telnyx_call_control_id && (
        <RecordingPanel callId={callId} />
      )}

      <nav id="inbound-detail-tabs" className="detail-tabs">
        <button
          id="inbound-tab-summary"
          className={tab === "summary" ? "is-active" : ""}
          onClick={() => setTab("summary")}
        >
          Summary
        </button>
        <button
          id="inbound-tab-transcript"
          className={tab === "transcript" ? "is-active" : ""}
          onClick={() => setTab("transcript")}
        >
          Transcript ({transcripts.length})
        </button>
        <button
          id="inbound-tab-tools"
          className={tab === "tools" ? "is-active" : ""}
          onClick={() => setTab("tools")}
        >
          Tool calls ({toolCalls.length})
        </button>
        <button id="inbound-tab-logs" className={tab === "logs" ? "is-active" : ""} onClick={() => setTab("logs")}>
          Logs ({logs.length})
        </button>
      </nav>

      {/* All tab panes are rendered at once but hidden onscreen unless
          active. The @media print rules in receipt.css un-hide them so
          "Print full record" produces a complete document covering
          summary + transcript + tools + logs. */}
      <div className={`tab-pane ${tab === "summary" ? "" : "is-hidden"}`}>
        <CallSummary
          call={call}
          callId={callId}
          counts={{ transcripts: transcripts.length, toolCalls: toolCalls.length }}
        />
      </div>

      <div className={`tab-pane ${tab === "transcript" ? "" : "is-hidden"}`}>
        <div id="inbound-transcript-pane" className="transcript-pane">
          {transcripts.length === 0 && <div className="muted">No transcript yet.</div>}
          {transcripts.map((t) => (
            <div key={t.id} id={`inbound-transcript-row-${t.id}`} className={`turn turn-${t.role}`}>
              <div className="turn-meta">
                {t.role} · {fmtTime(t.ts)}
              </div>
              <div className={`bubble bubble-${t.role}`}>{t.text}</div>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>
      </div>

      <div className={`tab-pane ${tab === "tools" ? "" : "is-hidden"}`}>
        <div id="inbound-tools-pane" className="tools-pane">
          {toolCalls.length === 0 && <div className="muted">No tool calls yet.</div>}
          {toolCalls.map((tc) => (
            <div key={tc.id} id={`inbound-tool-row-${tc.id}`} className={`tool-row tool-${tc.status}`}>
              <div className="tool-head">
                <strong>{tc.name}</strong>
                <span className={`tool-status status-${tc.status}`}>{tc.status}</span>
                {tc.duration_ms !== null && (
                  <span className="tool-dur">{tc.duration_ms} ms</span>
                )}
                <span className="tool-time">{fmtTime(tc.ts)}</span>
              </div>
              {tc.arguments && (
                <pre className="tool-json">args: {prettyJson(tc.arguments)}</pre>
              )}
              {tc.result && (
                <pre className="tool-json">result: {prettyJson(tc.result)}</pre>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={`tab-pane ${tab === "logs" ? "" : "is-hidden"}`}>
        <div id="inbound-logs-pane" className="logs-pane">
          {logs.length === 0 && <div className="muted">No logs yet.</div>}
          {logs.map((l) => (
            <div key={l.id} id={`inbound-log-row-${l.id}`} className={`log-row log-${l.level}`}>
              <span className={`log-level log-level-${l.level}`}>{l.level}</span>
              <span className="log-source">{l.source}</span>
              <span className="log-time">{fmtTime(l.ts)}</span>
              <span className="log-msg">{l.message}</span>
              {l.data && <pre className="log-data">{prettyJson(l.data)}</pre>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Probes the recording endpoint on mount. Telnyx finalises the audio file
 * a few seconds-to-minutes after the call ends; until then the API returns
 * 404 and the operator should see "Recording not yet available", not an
 * empty 0:00/0:00 audio control.
 */
function RecordingPanel({ callId }: { callId: number }) {
  const [state, setState] = useState<"probing" | "ready" | "missing">("probing");
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        // Range: bytes=0-0 lets us check the status without pulling the
        // whole MP3. The server still streams from Telnyx for the probe,
        // but only one byte goes over the wire.
        const r = await fetch(`${API_URL}/api/calls/${callId}/recording`, {
          method: "GET",
          headers: { Range: "bytes=0-0" },
          signal: ctrl.signal,
        });
        if (cancelled) return;
        setState(r.ok ? "ready" : "missing");
      } catch {
        if (!cancelled) setState("missing");
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [callId]);

  return (
    <div id="inbound-recording" className="recording">
      <div className="section-label">RECORDING</div>
      {state === "probing" && (
        <span className="recording-pending">Checking for recording…</span>
      )}
      {state === "missing" && (
        <span className="recording-pending">
          Not yet available — Telnyx is still finalising the audio.
        </span>
      )}
      {state === "ready" && (
        <>
          <audio
            id="inbound-recording-audio"
            controls
            preload="none"
            src={`${API_URL}/api/calls/${callId}/recording`}
          />
          <a
            id="inbound-recording-download"
            className="recording-download"
            href={`${API_URL}/api/calls/${callId}/recording`}
            download={`call-${callId}.mp3`}
          >
            Download
          </a>
        </>
      )}
    </div>
  );
}
